import React, { createContext, useState, useReducer, useMemo } from "react";
import { MetricRow, MetricReport, Query, stringifyQuery, customMetricRowFor } from "../../lib/api/charts";
import { Loading } from "../base";
import { CustomDashboardMeasures, CustomType } from "../../charts/CustomDashReports";

type MetricRowSet = { metric: MetricRow; loading: Loading; custom?: any };

export type CustomMetricRowContextState = {
  metric: {
    [index: string]: MetricRowSet;
  };
  getCustomMetricRow(report: MetricReport, query: Query, custom?: any): Partial<MetricRowSet>;
  retryCustomMetricRow(report: MetricReport, query: Query, custom?: any): void;
};

export const CustomMetricRowContext = createContext<CustomMetricRowContextState>({
  metric: {},
  getCustomMetricRow: () => ({}),
  retryCustomMetricRow: () => {},
});

type QueryAction =
  | { type: "PATCH_METRIC"; id: string; data: Partial<MetricRowSet> }
  | { type: "CLEAR_METRIC"; id: string };

const defaultCustom: string = "Overall Dealership > REVENUE > Total Dealership Sales";

const defaultMetricRow: MetricRowSet = {
  metric: {
    classification: "income",
    columns: [],
    label: "",
    data: [],
    aggregationOverride: "",
    measure: "",
    subHeaders: {},
  },
  loading: { loaded: false, loading: true, error: null },
  custom: CustomDashboardMeasures[defaultCustom].getMeasure(defaultCustom, CustomDashboardMeasures),
};

export const CustomMetricRowProvider = ({ children }) => {
  const memo = (report: MetricReport, query: Query, custom?: any) => `${report}-${stringifyQuery(query)}-${JSON.stringify(custom)}`;

  const [metric, dispatchMetricRow] = useReducer((state: { [index: string]: MetricRowSet }, action: QueryAction) => {
    switch (action.type) {
      case "PATCH_METRIC":
        return {
          ...state,
          [action.id]: {
            ...state[action.id],
            ...action.data,
          },
        };
      case "CLEAR_METRIC": {
        const { [action.id]: _, ...rest } = state;
        return rest;
      }
      default:
        return state;
    }
  }, {});

  const value = useMemo(
    () => ({
      metric,
      retryCustomMetricRow: (report: MetricReport, query: Query, custom?: CustomType) => {
        const id = memo(report, query, custom);
        dispatchMetricRow({ type: "CLEAR_METRIC", id });
      },
      getCustomMetricRow: (report: MetricReport, query: Query, custom?: CustomType) => {
        const id = memo(report, query, custom);
        const set = metric[id];

        if (set) {
          return set;
        }

        dispatchMetricRow({
          type: "PATCH_METRIC",
          id,
          data: defaultMetricRow,
        });

        customMetricRowFor(query, report, custom)
          .then(metric => {
            dispatchMetricRow({
              type: "PATCH_METRIC",
              id,
              data: {
                metric: metric,
                custom: custom,
                loading: {
                  loaded: true,
                  loading: false,
                  error: null,
                },
              },
            });
          })
          .catch(e => {
            dispatchMetricRow({
              type: "PATCH_METRIC",
              id,
              data: {
                loading: {
                  loaded: true,
                  loading: false,
                  error: e,
                },
              },
            });
          });

        return defaultMetricRow;
      },
    }),
    [metric],
  );

  return <CustomMetricRowContext.Provider value={value}>{children}</CustomMetricRowContext.Provider>;
};
